import {RuleSeverityBadge} from '@github-ui/code-scanning-shared/components/RuleSeverityBadge'
import {AgentIcon} from '@primer/octicons-react'
import {ActionList, Button, Spinner, Textarea} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {type FC, useCallback, useMemo, useState} from 'react'
import {getEntityCompositeID, getEntityFileLocation, getEntitySeverity, getEntitySource} from './entity-helpers'
import {type FixFindingsResponse, fixFindings} from './FindingActions'
import type {FindingEntity} from './types'
import styles from './Finding.module.css'

interface BulkFixDialogProps {
  findings: FindingEntity[]
  basePath: string
  onClose: () => void
}

export const BulkFixDialog: FC<BulkFixDialogProps> = ({findings, basePath, onClose}) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set(findings.map(f => getEntityCompositeID(f))))
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [result, setResult] = useState<FixFindingsResponse | null>(null)
  const [instructions, setInstructions] = useState('')

  const selectedFindings = useMemo(
    () => findings.filter(f => selectedIds.has(getEntityCompositeID(f))),
    [findings, selectedIds],
  )

  const noneSelected = selectedIds.size === 0

  const handleToggle = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const handleSubmit = useCallback(async () => {
    setIsSubmitting(true)

    try {
      const res = await fixFindings(basePath, selectedFindings, instructions || undefined)
      setResult(res)
    } catch {
      // TODO: error handling
    } finally {
      setIsSubmitting(false)
    }
  }, [basePath, selectedFindings, instructions])

  if (result) {
    return (
      <Dialog title="Fix with Copilot" onClose={onClose}>
        <p>
          {`Copilot is fixing ${selectedFindings.length} ${selectedFindings.length === 1 ? 'finding' : 'findings'}.`}
        </p>
        <Button as="a" href={result.task_url} leadingVisual={AgentIcon} className="mt-2">
          View Copilot task
        </Button>
      </Dialog>
    )
  }

  return (
    <Dialog
      title="Fix with Copilot"
      onClose={onClose}
      footerButtons={[
        {
          buttonType: 'primary',
          content: isSubmitting ? (
            <>
              <Spinner size="small" /> Fixing…
            </>
          ) : (
            `Fix ${selectedIds.size} ${selectedIds.size === 1 ? 'finding' : 'findings'} with Copilot`
          ),
          onClick: () => void handleSubmit(),
          disabled: noneSelected || isSubmitting,
          leadingVisual: isSubmitting ? undefined : AgentIcon,
        },
      ]}
    >
      <>
        <p className="tmp-mb-3 text-medium text-semibold color-fg-muted">
          Implement the suggestions in the findings below with Copilot coding agent. You will be added as co-author on
          any new commits.
        </p>
        <Textarea
          block
          rows={4}
          resize="none"
          value={instructions}
          className="rounded-3"
          onChange={e => setInstructions(e.target.value)}
          aria-label="Additional instructions"
          placeholder="Add additional instructions..."
        />
        <p className="tmp-mt-3 tmp-mb-1 text-small text-semibold color-fg-muted">
          {`${selectedIds.size} ${selectedIds.size === 1 ? 'finding' : 'findings'}`}
        </p>
        <ActionList
          showDividers
          role="listbox"
          selectionVariant="multiple"
          aria-label="Findings to fix"
          className="mt-2 border rounded-3"
        >
          {findings.map(finding => {
            const id = getEntityCompositeID(finding)
            const severity = getEntitySeverity(finding)
            const source = getEntitySource(finding)
            const fileLocation = getEntityFileLocation(finding)
            return (
              <ActionList.Item key={id} selected={selectedIds.has(id)} onSelect={() => handleToggle(id)}>
                <span>{finding.entity.title ?? 'Untitled finding'}</span>
                <ActionList.Description variant="block">
                  <div className="d-flex align-center gap-1">
                    {severity && <RuleSeverityBadge severity={severity} />}
                    {source && (
                      <>
                        <span className={styles.metaSeparator}>{'\u00B7'}</span>
                        <span>{source}</span>
                      </>
                    )}
                    {fileLocation && (
                      <>
                        <span className={styles.metaSeparator}>{'\u00B7'}</span>
                        <span className={styles.fileLocation}>{fileLocation}</span>
                      </>
                    )}
                  </div>
                </ActionList.Description>
              </ActionList.Item>
            )
          })}
        </ActionList>
      </>
    </Dialog>
  )
}
